/**
 * Configuration — Flag: Add / Edit save workflow, condition completeness, overlap checks.
 */

import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  ADDITIONAL_DETAILS_OPTIONS,
  CASE_MODULE_OPTIONS,
  CONDITION_OPERATOR_OPTIONS,
  SEVERITY_OPTIONS,
  applyDerivedFlagFields,
  attachConditionValueLabels,
  buildFlagScopeKey,
  getAvailableConditionFields,
  getConditionValueOptions,
  getOperatorsForField,
  getPermittedCaseModule,
  getReasonsForTarget,
  isAdditionalDetailsLockedToRequired,
  isCaseModuleReceivingAvailable,
  isConditionValueAvailable,
  isRepeatedFixedRequirement,
  isSecurityTarget,
  isTargetDefinitionsConfirmed,
  nextFlagRuleId,
  normalizeFlagForm,
  operatorIsMulti,
} from './flagConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function flagActor() {
  return { ...ACTOR };
}

export function canAccessFlagConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewFlagConfiguration(auth) {
  return canAccessFlagConfiguration(auth);
}

export function canAddFlagRule(auth) {
  return canAccessFlagConfiguration(auth);
}

export function canEditFlagRule(auth) {
  return canAccessFlagConfiguration(auth);
}

export function canSaveFlagRule(auth) {
  return canAccessFlagConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.errors[key]) result.errors[key] = message;
  if (!result.summary.includes(message)) result.summary.push(message);
}

function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

function uniqueSorted(values = []) {
  return [...new Set((values || []).filter((v) => v != null && v !== ''))].sort();
}

function conditionValues(condition) {
  return uniqueSorted(condition?.values || []);
}

/** Normalise Equals / single-value Is Any Of (and matching exclusions) for duplicate detection. */
function normalizeOperatorForCompare(operator, values) {
  const vals = uniqueSorted(values);
  if (operator === 'equals' || (operator === 'is_any_of' && vals.length === 1)) {
    return { operator: 'equals', values: vals.slice(0, 1) };
  }
  if (operator === 'does_not_equal' || (operator === 'is_not_any_of' && vals.length === 1)) {
    return { operator: 'does_not_equal', values: vals.slice(0, 1) };
  }
  return { operator, values: vals };
}

function conditionFingerprint(condition) {
  const normalized = normalizeOperatorForCompare(condition.operator, condition.values);
  return [condition.field || '', normalized.operator, normalized.values.join(',')].join('|');
}

function sortedConditionFingerprints(conditions = []) {
  return [...(conditions || []).map(conditionFingerprint)].sort();
}

function sameSet(a = [], b = []) {
  const left = uniqueSorted(a);
  const right = uniqueSorted(b);
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function getFieldUniverse(form, field) {
  return getConditionValueOptions(field, form).map((o) => o.value);
}

export function resolveFieldAllowedValues(conditions = [], field, universe = []) {
  let allowed = new Set(universe);
  const fieldConditions = (conditions || []).filter((condition) => condition.field === field);
  fieldConditions.forEach((condition) => {
    const values = conditionValues(condition);
    if (!condition.operator || !values.length) return;
    if (condition.operator === 'equals' || (condition.operator === 'is_any_of' && values.length === 1)) {
      allowed = new Set([...allowed].filter((value) => value === values[0]));
    } else if (condition.operator === 'does_not_equal' || (condition.operator === 'is_not_any_of' && values.length === 1)) {
      allowed = new Set([...allowed].filter((value) => value !== values[0]));
    } else if (condition.operator === 'is_any_of') {
      allowed = new Set([...allowed].filter((value) => values.includes(value)));
    } else if (condition.operator === 'is_not_any_of') {
      allowed = new Set([...allowed].filter((value) => !values.includes(value)));
    }
  });
  return [...allowed];
}

export function conditionsAreImpossible(conditions = [], form) {
  const fields = [...new Set((conditions || []).map((c) => c.field).filter(Boolean))];
  return fields.some((field) => {
    const universe = getFieldUniverse(form, field);
    if (!universe.length) return false;
    return resolveFieldAllowedValues(conditions, field, universe).length === 0;
  });
}

export function getImplicitAllowedValues(form, field) {
  const universe = getFieldUniverse(form, field);
  if (!universe.length) return [];
  return resolveFieldAllowedValues(form.conditions, field, universe);
}

function sameScope(a, b) {
  return a.flagSource === b.flagSource && a.module === b.module && a.targetType === b.targetType;
}

function flagSettingsKey(rule) {
  return [
    rule.severity || '',
    rule.caseModule || '',
    rule.flagSource === 'user_flag' ? rule.additionalDetails || '' : '',
  ].join('|');
}

function isExactDuplicate(a, b) {
  if (!sameScope(a, b)) return false;
  if (flagSettingsKey(a) !== flagSettingsKey(b)) return false;
  return sameSet(sortedConditionFingerprints(a.conditions), sortedConditionFingerprints(b.conditions));
}

/** Overlap when the same eligible input could match both Rules (including future values for negative sets). */
export function rulesOverlap(a, b) {
  if (!sameScope(a, b)) return false;
  const aEmpty = !(a.conditions || []).length;
  const bEmpty = !(b.conditions || []).length;
  if (aEmpty || bEmpty) return true;

  const fields = [...new Set([
    ...(a.conditions || []).map((c) => c.field),
    ...(b.conditions || []).map((c) => c.field),
  ].filter(Boolean))];

  if (!fields.length) return true;

  return fields.every((field) => {
    const universe = getFieldUniverse(a, field);
    if (!universe.length) return true;
    const aValues = getImplicitAllowedValues(a, field);
    const bValues = getImplicitAllowedValues(b, field);
    const aOpenEnded = (a.conditions || []).some(
      (c) => c.field === field && (c.operator === 'does_not_equal' || c.operator === 'is_not_any_of'),
    );
    const bOpenEnded = (b.conditions || []).some(
      (c) => c.field === field && (c.operator === 'does_not_equal' || c.operator === 'is_not_any_of'),
    );
    if (aOpenEnded || bOpenEnded) {
      const shared = aValues.some((value) => bValues.includes(value));
      if (shared) return true;
      return aOpenEnded && bOpenEnded;
    }
    return aValues.some((value) => bValues.includes(value));
  });
}

/** Conflict = overlapping coverage with different Flag settings. Reported with overlap message per validation table. */
export function rulesConflict(a, b) {
  return rulesOverlap(a, b) && flagSettingsKey(a) !== flagSettingsKey(b);
}

function publishedBaselineConditionsLength(allRules, excludeId) {
  const current = (allRules || []).find((rule) => rule.id === excludeId);
  return (current?.conditions || []).length;
}

export function flagRuleFingerprint(rule) {
  const snapshot = ruleSnapshotFromForm(rule);
  return JSON.stringify({
    ruleName: snapshot.ruleName,
    description: snapshot.description,
    flagSource: snapshot.flagSource,
    module: snapshot.module,
    targetType: snapshot.targetType,
    severity: snapshot.severity,
    caseModule: snapshot.caseModule,
    additionalDetails: snapshot.additionalDetails,
    conditions: sortedConditionFingerprints(snapshot.conditions),
  });
}

export function collectFlagSaveErrors(
  formData,
  allRules,
  excludeId,
  {
    originalLastUpdated,
    originalScopeKey,
    canSave = true,
    checksAvailable = true,
  } = {},
) {
  const result = { errors: {}, summary: [] };

  if (!checksAvailable) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  if (!canSave) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }

  let form;
  try {
    form = applyDerivedFlagFields(normalizeFlagForm(formData));
  } catch {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeFlagForm);

  if (!String(form.ruleName || '').trim()) addError(result, 'ruleName', 'Enter a Rule Name.');
  else if (others.some((rule) => namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'This Rule Name is already used.');
  }

  if (!String(form.description || '').trim()) addError(result, 'description', 'Enter an Internal Description.');

  if (!form.flagSource) addError(result, 'flagSource', 'Choose a Flag Source.');

  if (!form.module || !form.targetType) {
    addError(result, 'targetType', 'Choose a compatible Module and Target Type.');
  } else {
    const permitted = getPermittedCaseModule(form.flagSource, form.targetType);
    if (!permitted) addError(result, 'targetType', 'Choose a compatible Module and Target Type.');
  }

  if ((!isTargetDefinitionsConfirmed(form.targetType) || isSecurityTarget(form.targetType)) && form.targetType) {
    addError(result, 'sourceDefinition', 'Required Flag information is not defined or available.');
  }

  if (!form.severity) addError(result, 'severity', 'Choose a Severity supported by this Rule’s coverage.');
  else if (!SEVERITY_OPTIONS.some((o) => o.value === form.severity)) {
    addError(result, 'severity', 'Choose a Severity supported by this Rule’s coverage.');
  }

  const permittedCase = getPermittedCaseModule(form.flagSource, form.targetType);
  if (!form.caseModule) {
    addError(result, 'caseModule', 'Choose the Case Module permitted for this Flag.');
  } else if (!CASE_MODULE_OPTIONS.some((o) => o.value === form.caseModule)) {
    addError(result, 'caseModule', 'Choose the Case Module permitted for this Flag.');
  } else if (permittedCase && form.caseModule !== permittedCase) {
    if (permittedCase === 'moderation' && form.caseModule === 'fraud_security') {
      addError(result, 'caseModule', 'Content Flags must use Moderation.');
    } else if (permittedCase === 'fraud_security' && form.caseModule === 'moderation') {
      addError(result, 'caseModule', 'Security Flags must use Fraud & Security.');
    } else {
      addError(result, 'caseModule', 'Choose the Case Module permitted for this Flag.');
    }
  } else if (!isCaseModuleReceivingAvailable(form.caseModule)) {
    addError(result, 'caseModule', 'The selected module cannot receive this Flag yet.');
  }

  if (form.flagSource === 'user_flag') {
    const lockedRequired = isAdditionalDetailsLockedToRequired(form);
    if (lockedRequired) {
      if (form.additionalDetails !== 'required') {
        addError(result, 'additionalDetails', 'Other requires Additional Details.');
      }
    } else if (!form.additionalDetails) {
      addError(result, 'additionalDetails', 'Choose an Additional Details requirement.');
    } else if (!ADDITIONAL_DETAILS_OPTIONS.some((o) => o.value === form.additionalDetails)) {
      addError(result, 'additionalDetails', 'Choose an Additional Details requirement.');
    }
  }

  if (!String(form.changeReason || '').trim()) addError(result, 'changeReason', 'Enter a Change Reason.');

  if (excludeId && originalScopeKey) {
    const currentKey = buildFlagScopeKey(form);
    if (currentKey !== originalScopeKey) {
      addError(result, 'fixedScope', 'These scope fields cannot be changed.');
    }
  }

  const availableFields = getAvailableConditionFields(form).map((o) => o.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    const incomplete = !condition.field || !condition.operator || !values.length;
    if (incomplete) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    if (!availableFields.includes(condition.field)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
      return;
    }
    if (isRepeatedFixedRequirement(form, condition)) {
      addError(result, key, 'This Condition repeats a fixed requirement.');
      return;
    }
    const allowedOps = getOperatorsForField(condition.field).map((o) => o.value);
    if (!allowedOps.includes(condition.operator) || !CONDITION_OPERATOR_OPTIONS.some((o) => o.value === condition.operator)) {
      addError(result, key, 'Choose a compatible Operator and Value.');
      return;
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Choose a compatible Operator and Value.');
    }
    if (uniqueSorted(condition.values || []).length !== (condition.values || []).length) {
      addError(result, key, 'Remove repeated values.');
    }
    if (values.some((value) => !isConditionValueAvailable(condition.field, value, form))) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is duplicated.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsAreImpossible(form.conditions, form)) {
    addError(result, 'conditions', 'These Conditions cannot be true together.');
  }

  try {
    const sameScopeRules = others.filter((rule) => sameScope(rule, form));
    const duplicate = sameScopeRules.find((rule) => isExactDuplicate(rule, form));
    if (duplicate) {
      addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);
    }

    const formEmpty = !(form.conditions || []).length;
    const unconditionalPeer = sameScopeRules.find((rule) => !(rule.conditions || []).length);
    const hadConditionsOriginally = Boolean(
      excludeId && (publishedBaselineConditionsLength(allRules, excludeId) > 0),
    );

    if (!duplicate) {
      if (unconditionalPeer) {
        addError(result, 'overlap', 'A Rule with No Conditions already covers this scope.');
      } else if (formEmpty && sameScopeRules.some((rule) => (rule.conditions || []).length)) {
        if (hadConditionsOriginally) {
          addError(result, 'overlap', 'Removing these Conditions would create an overlap.');
        } else {
          const peer = sameScopeRules.find((rule) => (rule.conditions || []).length);
          addError(result, 'overlap', `This Rule overlaps ${peer.id}.`);
        }
      } else {
        const overlapping = sameScopeRules.find((rule) => rulesOverlap(rule, form));
        if (overlapping) {
          // Conflict (different settings) and overlap (same settings) share this message.
          addError(result, rulesConflict(overlapping, form) ? 'conflict' : 'overlap', `This Rule overlaps ${overlapping.id}.`);
        }
      }
    }

    if ((form.conditions || []).length && form.flagSource === 'user_flag') {
      const reasons = getReasonsForTarget(form.targetType).map((r) => r.value);
      if (reasons.length) {
        const allowedReasons = resolveFieldAllowedValues(form.conditions, 'reason', reasons);
        const hasReasonCondition = (form.conditions || []).some((c) => c.field === 'reason');
        if (hasReasonCondition && allowedReasons.length === 0) {
          addError(result, 'conditions', 'These Conditions cannot be true together.');
        }
      }
    }
  } catch {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
  }

  if (excludeId && originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule was updated by another Team Member. Review the current Rule before saving.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = applyDerivedFlagFields(normalizeFlagForm(formData));
  return {
    ruleName: String(form.ruleName || '').trim(),
    description: String(form.description || '').trim(),
    flagSource: form.flagSource,
    module: form.module,
    targetType: form.targetType,
    severity: form.severity,
    caseModule: form.caseModule,
    additionalDetails: form.flagSource === 'user_flag' ? form.additionalDetails : '',
    conditions: (form.conditions || []).map((condition) => {
      const labeled = attachConditionValueLabels(condition, form);
      return {
        id: labeled.id,
        field: labeled.field,
        operator: labeled.operator,
        values: conditionValues(labeled),
        valueLabels: labeled.valueLabels || {},
      };
    }),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeFlagForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeFlagForm(rule);
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const id = payload.id || publishedBaseline?.id || nextFlagRuleId(allRules);

  if (!isNew) {
    const existing = (allRules || []).find((rule) => rule.id === id) || publishedBaseline;
    if (existing && flagRuleFingerprint(existing) === flagRuleFingerprint({ ...snapshot, id })) {
      return {
        rules: allRules,
        record: normalizeFlagForm(existing),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    changeReason: String(payload.changeReason || '').trim(),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Flag',
      sourceModuleRef: 'configuration_flag',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'name',
        'description',
        'flagSource',
        'module',
        'targetType',
        'severity',
        'caseModule',
        'additionalDetails',
        'conditions',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  const rules = isNew
    ? [...allRules.filter((rule) => rule.id !== record.id), record]
    : allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule));

  return { rules, record, unchanged: false, isNew };
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = mergeRulesAfterSave(allRules, payload, publishedBaseline);
  return result.rules;
}

export function saveFlagRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectFlagSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated: options.originalLastUpdated ?? publishedBaseline?.lastUpdated,
    originalScopeKey: options.originalScopeKey
      ?? (publishedBaseline ? buildFlagScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
  return {
    success: true,
    errors: {},
    summary: [],
    rules: merged.rules,
    record: merged.record,
    unchanged: Boolean(merged.unchanged),
    isNew: Boolean(merged.isNew),
  };
}
